import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui-kit/alert-dialog';
import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';

/**
 * ConfirmationModal Component
 *
 * A reusable modal dialog that prompts users to confirm or cancel an action.
 * Built using AlertDialog components for accessibility and consistent styling.
 *
 * Features:
 * - Controlled visibility state
 * - Customizable title and description
 * - Standardized cancel and confirm buttons
 * - Accessible design with proper ARIA attributes
 * - High z-index to ensure visibility above other UI elements
 * - Responsive layout with maximum width
 *
 * Props:
 * @param {boolean} open - Controls whether the modal is displayed
 * @param {(open: boolean) => void} onOpenChange - Callback when modal open state changes
 * @param {string} title - The title displayed in the modal header
 * @param {string} description - The descriptive text explaining the action to confirm
 * @param {() => void} onConfirm - Callback function executed when the user confirms the action
 *
 * @example
 * // Basic usage
 * <ConfirmationModal
 *   open={showDeleteDialog}
 *   onOpenChange={setShowDeleteDialog}
 *   title="Delete Item"
 *   description="Are you sure you want to delete this item? This action cannot be undone."
 *   onConfirm={handleDelete}
 * />
 */

export interface ConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  onConfirm: () => unknown;
  confirmText?: string;
  cancelText?: string;
  confirmClassName?: string;
  /**
   * If true, the modal will not automatically close after confirmation.
   * The parent component is responsible for closing the modal if needed.
   * Default is false (modal will auto-close after confirmation).
   */
  preventAutoClose?: boolean;
  /** Hide the cancel button (e.g. an acknowledgement-only dialog). */
  hideCancel?: boolean;
  /** Extra content under the description (e.g. a required reason field). */
  children?: React.ReactNode;
  /** Disables the confirm button (e.g. until a required field is filled). */
  confirmDisabled?: boolean;
  /** Shows a spinner on the confirm button and disables it (e.g. while the action runs). */
  confirmLoading?: boolean;
}

export const ConfirmationModal = ({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmClassName,
  preventAutoClose = false,
  hideCancel = false,
  children,
  confirmDisabled = false,
  confirmLoading = false,
}: Readonly<ConfirmationModalProps>) => {
  const { t } = useTranslation();

  const handleConfirmClick = () => {
    // onConfirm may reject (e.g. a mutation the caller doesn't await); the mutation's own
    // global error handler already shows a toast, so just prevent an unhandled rejection here.
    Promise.resolve(onConfirm()).catch(() => {
      // Error toast is already shown by the mutation's global error handler.
    });
    if (!preventAutoClose) {
      onOpenChange(false);
    }
  };
  const handleCancelClick = () => {
    onOpenChange(false);
  };

  const isDestructiveConfirm = Boolean(
    confirmClassName?.includes('bg-destructive') || confirmClassName?.includes('destructive')
  );

  return (
    // Component Map › Confirmations: a stock alert-dialog. No close button, and clicking the overlay
    // doesn't dismiss it (Radix AlertDialog); Cancel or the action closes it. The footer stacks on phones.
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-[420px]">
        <AlertDialogHeader className="text-left">
          <AlertDialogTitle className="[overflow-wrap:anywhere]">{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="[overflow-wrap:anywhere]">{description}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {children}

        <AlertDialogFooter className="max-sm:[&>button]:w-full">
          {!hideCancel && (
            <Button type="button" variant="outline" onClick={handleCancelClick}>
              {t(cancelText)}
            </Button>
          )}
          <Button
            type="button"
            variant={isDestructiveConfirm ? 'destructive' : 'default'}
            className={cn(!isDestructiveConfirm && confirmClassName)}
            onClick={handleConfirmClick}
            loading={confirmLoading}
            disabled={confirmDisabled || confirmLoading}
          >
            {t(confirmText)}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
